import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Action } from '../../../actions';
import { isEditorOpened } from './utils';
import { getClientAndResourceIds, getPageSpreads } from '../utils/helpers';
import { ExternalPublicationData } from '../reducer/combo';
import { reset } from './ui';
import { getData } from './sources';
import { changePageSpread } from './pagesController';
import { createNotification } from '../../NotificationBar/actions';
import { Api } from '../../../typings/api';
import ApiUtils from '../../../utils/api';
import { ConfigsData } from '../reducer';
import { EditorMessageTypes, ZoomType } from '../constants';
import Utils from '../../../utils';
import RoutesUtils from '../../../utils/routes';

export enum ComboActionKeys {
	CREATE_COMBO = '@@editor/combo/CREATE_COMBO',
	UPDATE_COMBO_PERCENTAGE = '@@editor/combo/UPDATE_COMBO_PERCENTAGE',
	FINISH_COMBO = '@@editor/combo/FINISH_COMBO',
	GET_EXTERNAL_PUBLICATION_DATA = '@@editor/combo/GET_EXTERNAL_PUBLICATION_DATA',
	SET_EXTERNAL_PUBLICATION_DATA = '@@editor/combo/SET_EXTERNAL_PUBLICATION_DATA',
	RESET_EXTERNAL_PUBLICATION_DATA = '@@editor/combo/RESET_EXTERNAL_PUBLICATION_DATA',
	SELECT_EXTERNAL_SPREAD = '@@editor/combo/SELECT_EXTERNAL_SPREAD',
	SELECT_ALL_EXTERNAL_SPREADS = '@@editor/combo/SELECT_ALL_EXTERNAL_SPREADS',
	DESELECT_ALL_EXTERNAL_SPREADS = '@@editor/combo/DESELECT_ALL_EXTERNAL_SPREADS',
	ZOOM_EXTERNAL_SPREADS = '@@editor/combo/ZOOM_EXTERNAL_SPREADS',
}

interface CreateComboAction extends Action {
	type: ComboActionKeys.CREATE_COMBO;
}

interface UpdateComboPercentageAction extends Action {
	type: ComboActionKeys.UPDATE_COMBO_PERCENTAGE;
	percentage: number;
}

interface FinishComboAction extends Action {
	type: ComboActionKeys.FINISH_COMBO;
}

interface GetExternalPublicationDataAction extends Action {
	type: ComboActionKeys.GET_EXTERNAL_PUBLICATION_DATA;
}

interface SetExternalPublicationDataAction extends Action {
	type: ComboActionKeys.SET_EXTERNAL_PUBLICATION_DATA;
	data: ExternalPublicationData;
}

interface ResetExternalPublicationDataAction extends Action {
	type: ComboActionKeys.RESET_EXTERNAL_PUBLICATION_DATA;
}

interface SelectExternalSpreadAction extends Action {
	type: ComboActionKeys.SELECT_EXTERNAL_SPREAD;
	spreadIndex: number;
	allSpreads: number[][];
	isCtrlPressed?: boolean;
	isShiftPressed?: boolean;
}

interface SelectAllExternalSpreadsAction extends Action {
	type: ComboActionKeys.SELECT_ALL_EXTERNAL_SPREADS;
}

interface DeselectAllExternalSpreadsAction extends Action {
	type: ComboActionKeys.DESELECT_ALL_EXTERNAL_SPREADS;
}

interface ZoomExternalSpreadsAction extends Action {
	type: ComboActionKeys.ZOOM_EXTERNAL_SPREADS;
	zoom: ZoomType;
	autoItemsInRow: number;
}

export type ComboAction =
	CreateComboAction |
	UpdateComboPercentageAction |
	FinishComboAction |
	GetExternalPublicationDataAction |
	SetExternalPublicationDataAction |
	ResetExternalPublicationDataAction |
	SelectExternalSpreadAction |
	SelectAllExternalSpreadsAction |
	DeselectAllExternalSpreadsAction |
	ZoomExternalSpreadsAction;

const setExternalPublicationData = (data: ExternalPublicationData): SetExternalPublicationDataAction => ({
	type: ComboActionKeys.SET_EXTERNAL_PUBLICATION_DATA,
	data,
});

export const resetExternalPublicationData = (): ResetExternalPublicationDataAction => ({
	type: ComboActionKeys.RESET_EXTERNAL_PUBLICATION_DATA,
});

export const selectExternalSpread = (
	spreadIndex: number,
	allSpreads: number[][],
	isCtrlPressed?: boolean,
	isShiftPressed?: boolean,
): SelectExternalSpreadAction => ({
	type: ComboActionKeys.SELECT_EXTERNAL_SPREAD,
	spreadIndex,
	allSpreads,
	isCtrlPressed,
	isShiftPressed,
});

export const selectAllExternalSpreads = (): SelectAllExternalSpreadsAction => ({
	type: ComboActionKeys.SELECT_ALL_EXTERNAL_SPREADS,
});

export const deselectAllExternalSpreads = (): DeselectAllExternalSpreadsAction => ({
	type: ComboActionKeys.DESELECT_ALL_EXTERNAL_SPREADS,
});

export const zoomExternalSpreads = (zoom: ZoomType, autoItemsInRow: number): ZoomExternalSpreadsAction => ({
	type: ComboActionKeys.ZOOM_EXTERNAL_SPREADS,
	zoom,
	autoItemsInRow,
});

export const createCombo = (resource: Api.DriveItem) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch({
			type: ComboActionKeys.CREATE_COMBO,
		});

		const {
			editor: {
				label: { label },
				pagesController: { spreads },
			},
		} = getState();

		const { clientId, tempGId: resourceId } = getClientAndResourceIds();
		const resourcesGIds = [resourceId, resource.id];
		const spreadsAmountBeforeCombo = spreads.length;

		const { status } = await ApiUtils.createCombo(clientId, resourceId, resourcesGIds, label);

		const onProgressUpdate = (percentage: number) => {
			const action: UpdateComboPercentageAction = {
				type: ComboActionKeys.UPDATE_COMBO_PERCENTAGE,
				percentage
			};
			dispatch(action);
		};

		const onProgressError = () => {
			dispatch({
				type: ComboActionKeys.FINISH_COMBO,
			});
			dispatch(createNotification('error.combine_error'));
		};

		const onProgressFinish = async () => {
			await dispatch(finishComboCreation(clientId, resourceId, spreadsAmountBeforeCombo));
		};

		const shouldCancelProgress = (state: StoreState) => {
			return !isEditorOpened(state);
		};

		const onProgressCancel = () => {
			dispatch(reset(true));
		};

		if (![
			Api.AssetItemStatus.LIVE,
			Api.AssetItemStatus.ERROR,
		].includes(status)) {
			await ApiUtils.getPublicationProgress(clientId, resourceId, onProgressUpdate, onProgressError, onProgressFinish, shouldCancelProgress, onProgressCancel);
		} else {
			if (status === Api.AssetItemStatus.LIVE) {
				onProgressFinish();
			}

			if (status === Api.AssetItemStatus.ERROR) {
				onProgressError();
			}
		}
	} catch (error) {
		dispatch({
			type: ComboActionKeys.FINISH_COMBO,
		});
		dispatch(createNotification('error.combine_error'));
	}
};

export const getExternalPublicationData = (globalId: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const action: GetExternalPublicationDataAction = {
			type: ComboActionKeys.GET_EXTERNAL_PUBLICATION_DATA,
		};
		dispatch(action);

		const { clientId } = getClientAndResourceIds();
		const {
			enrichmentInfo,
			enrichmentInfo: {
				configsPath = '',
				thumbnailPath,
				spreadPath,
				displayMode,
				firstFrameMode,
				totalPages,
				format,
			},
			pagesData,
		} = await ApiUtils.getIndesignEditorData(clientId, globalId, false);

		const isPdfProposal = format === Api.PublicationFormat.PDF_EDITABLE;
		let spreads: number[][] = [];
		let parsedConfigs: ConfigsData | undefined;

		if (!isPdfProposal && configsPath) {
			const configs = await ApiUtils.getEditorResource(globalId, configsPath);
			parsedConfigs = JSON.parse(configs);
		}

		spreads = getPageSpreads(
			displayMode,
			firstFrameMode,
			totalPages,
			isPdfProposal ? pagesData.map(page => ({ showAlone: false })) : parsedConfigs!.pagesData,
			enrichmentInfo.dimensions,
		);

		const data: ExternalPublicationData = {
			globalId,
			spreads,
			thumbnails: pagesData.map(({ label, position }) => ({ label, position })),
			thumbnailPath,
			spreadPath,
		};

		if (parsedConfigs) {
			data.configs = parsedConfigs;
		}

		dispatch(setExternalPublicationData(data));
	} catch (error) {
		dispatch(createNotification('error.fetch_publication_data'));
	}
};

export const createComboWithExternalSpreads = (callback) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch({
			type: ComboActionKeys.CREATE_COMBO,
		});

		const {
			editor: {
				pagesController: { spreads },
				combo: {
					externalPublication: {
						globalId: sourceId,
						spreads: externalSpreads,
						selection: { spreadIndices: selectedSpreadIndices },
					},
				},
			},
			driveList: { driveItems },
		} = getState();

		const { clientId, tempGId: resourceId, originalGId } = getClientAndResourceIds();
		const externalPages = selectedSpreadIndices.reduce((acc: number[], current: number) => {
			acc = acc.concat(externalSpreads[current]);
			return acc;
		}, []);
		const spreadsAmountBeforeCombo = spreads.length;
		const { globalId } = await ApiUtils.createComboWithExternalSpreads(clientId, resourceId, sourceId as number, externalPages);
		const isPlaylist = originalGId && driveItems[originalGId] && driveItems[originalGId].format === Api.PublicationFormat.EPUB_PLAYLIST;

		if (!isPlaylist && globalId) {
			if (Utils.isEmbedMode()) {
				const parentOrigin = new URL(document.referrer).origin;
				window.parent.postMessage({ type: EditorMessageTypes.NEW_PLAYLIST_ID, globalId }, parentOrigin);
			} else {
				RoutesUtils.historyPush(RoutesUtils.getPathEditor(clientId, globalId));
			}
		}

		await dispatch(finishComboCreation(clientId, resourceId, spreadsAmountBeforeCombo));

		if (callback && typeof callback === 'function') {
			callback();
		}
	} catch (error) {
		dispatch(createNotification('error.combine_error'));
	} finally {
		dispatch({
			type: ComboActionKeys.FINISH_COMBO,
		});
	}
};

const finishComboCreation = (
	clientId: number,
	resourceId: number,
	spreadsAmountBeforeCombo: number,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	dispatch(reset(true));
	await dispatch(getData(clientId, resourceId, false));

	if (isEditorOpened()) {
		const {
			editor: {
				pagesController: { spreads },
			},
		} = getState();
		const spreadsAmountAfterCombo = spreads.length;
		const activeSpread = spreadsAmountBeforeCombo === spreadsAmountAfterCombo
			? spreads[spreads.length - 1]
			: spreads[spreadsAmountBeforeCombo];
		const activeSpreadIndex = spreads.indexOf(activeSpread);
		dispatch(changePageSpread(activeSpreadIndex, false));
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions/combo.ts